// web/src/routes/schedule/Shifts.jsx
// The roster — who is working, a week at a time.
//
// This sits a level above Availability. Availability says when a trainer can
// TEACH, and generates the slots clients book into. This says who is at the
// barn doing the work: feed, turnout, stalls, night check. Nobody books into a
// shift and a shift generates nothing.
//
// One idea carries the page, and it is the one worth getting right: a schedule
// you are building is not a schedule anybody else can see. Everything here is a
// draft until Publish, and Publish does two things at once — it makes the week
// visible, and it sends each person their own shifts. A barn does not check
// apps, it checks phones.
//
// The week is rolling, starting today, the same as the board. On a Thursday a
// Sunday-aligned week spends three of its columns on days that already
// happened.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import ShiftGrid from '../../components/shifts/ShiftGrid';
import DayGantt from '../../components/shifts/DayGantt';
import ShiftEditor from '../../components/shifts/ShiftEditor';
import CopyWeekDialog from '../../components/shifts/CopyWeekDialog';
import RequestsInbox from '../../components/shifts/RequestsInbox';
import RequestTimeOff from '../../components/shifts/RequestTimeOff';
import ApplyRoutineDialog from '../../components/shifts/ApplyRoutineDialog';
import { addDaysISO, weekFrom, unannouncedCount, formatMinutes, toMinutes, unionMinutes, timeRange } from '../../lib/shifts';
import { fmtDate } from '../../lib/dates';

const VIEW_ROLES = ['owner', 'manager', 'trainer', 'groom'];
const EDIT_ROLES = ['owner', 'manager'];

/** "Sep 7 – Sep 13, 2026" */
function rangeLabel(from, to) {
  return `${fmtDate(from, 'short')} – ${fmtDate(to, 'medium')}`;
}

/** "Monday, Sep 7" — the day view's own heading. */
function longDay(iso) {
  return fmtDate(iso, 'dayMediumYear');
}

/**
 * What actually went wrong, with the code, where the click was.
 *
 * Not "Couldn't save that. Try again." — "try again" is wrong advice for a 403
 * and useless for a 404, and a message that hides the code makes the barn ring
 * up to report something we could have read off the screen.
 */
function friendly(err) {
  const code = err?.body?.error || err?.message;
  switch (code) {
    // The request never reached the barn. Said as that, with the browser's own
    // words kept in brackets — "Load failed" on its own reads as the app being
    // broken, and it is the signal.
    case 'no_connection':
      return `That didn't save — couldn't reach the server, so nothing changed. Check your signal and try again (${err.detail}).`;
    case 'timed_out':
      return "That didn't save — the server took too long to answer, so nothing changed. Check your signal and try again.";
    case 'not_allowed':
      return 'Only an owner or manager can change the schedule.';
    case 'not_staff':
      return 'That person is not on the team — clients and riders cannot be scheduled.';
    case 'invalid_time':
      return "That time didn't read as a time.";
    case 'end_before_start':
      return 'The end is before the start.';
    case 'invalid_date':
      return "That date didn't read as a date.";
    case 'unknown_shift_type':
      return 'That kind of work no longer exists.';
    case 'already_decided':
      return 'Somebody already answered that request.';
    case 'same_week':
      return 'That is the week you are copying from.';
    case 'not_found':
      return 'That shift is already gone.';
    default:
      return `That didn't save — ${code || 'no reason given'}${err?.status ? ` (${err.status})` : ''}.`;
  }
}

export default function ShiftsPage() {
  const qc = useQueryClient();
  const { activeRole, user } = useAuth();
  const canView = VIEW_ROLES.includes(activeRole);
  const canEdit = EDIT_ROLES.includes(activeRole);

  const [searchParams, setSearchParams] = useSearchParams();
  const anchor = searchParams.get('from'); // null until the server says "today"
  const view = searchParams.get('view') === 'day' ? 'day' : 'week';
  const groupBy = searchParams.get('by') === 'work' ? 'work' : 'person';

  const [editor, setEditor] = useState(null); // { shift } | { date, userId }
  const [copying, setCopying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [inbox, setInbox] = useState(false);
  const [asking, setAsking] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [clearingPlan, setClearingPlan] = useState(null);
  const [planPreview, setPlanPreview] = useState(null);
  const [error, setError] = useState(null);

  const from = anchor;
  const to = from ? (view === 'day' ? from : addDaysISO(from, 6)) : null;

  const weekQ = useQuery({
    queryKey: ['shifts', view, from ?? 'current'],
    // Before the first response there is no barn-local date to ask for, so the
    // first call uses the browser's — which is only ever used to find out what
    // the barn thinks today is, and is replaced by the answer.
    queryFn: () => {
      const start = from || new Date().toISOString().slice(0, 10);
      const end = view === 'day' ? start : addDaysISO(start, 6);
      return api.get(`/shifts?from=${start}&to=${end}`);
    },
    enabled: canView,
    retry: false,
  });

  // Only fetched when somebody can actually apply one.
  const routinesQ = useQuery({
    queryKey: ['routines'],
    queryFn: () => api.get('/routines'),
    enabled: canEdit,
    retry: false,
  });

  const requestsQ = useQuery({
    queryKey: ['time-off-requests'],
    queryFn: () => api.get('/time-off/requests'),
    enabled: canEdit,
    retry: false,
  });

  // The barn's today, latched from the first response that carries it.
  const [today, setToday] = useState(null);
  useEffect(() => {
    if (weekQ.data?.today) setToday(weekQ.data.today);
  }, [weekQ.data]);

  // Lock the URL onto a concrete date once we know it, so the steppers below
  // are pure ISO arithmetic rather than guesses about the barn's clock.
  useEffect(() => {
    if (!anchor && today) {
      const p = new URLSearchParams(searchParams);
      p.set('from', today);
      setSearchParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, today]);

  function step(n) {
    const base = anchor || today;
    if (!base) return;
    setParams({ from: addDaysISO(base, n * (view === 'day' ? 1 : 7)) });
  }

  /** Merge into the URL, dropping anything set to null. */
  function setParams(next) {
    const p = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setSearchParams(p, { replace: true });
  }

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['shifts'] });
    qc.invalidateQueries({ queryKey: ['routines'] });
    qc.invalidateQueries({ queryKey: ['time-off-requests'] });
    // A shift is not a slot, but time off is shared between them — approving a
    // request has to reach the availability calendar as well.
    qc.invalidateQueries({ queryKey: ['availability'] });
    qc.invalidateQueries({ queryKey: ['availability-day'] });
  };

  const saveShift = useMutation({
    mutationFn: ({ id, body }) => (id ? api.patch(`/shifts/${id}`, body) : api.post('/shifts', body)),
    // Trying again clears the last failure first. Without this the second
    // attempt ran under the first one's message, so a phone showed "That
    // didn't save" and "Saving…" at the same time — both true, and unreadable.
    onMutate: () => setError(null),
    onSuccess: () => {
      setEditor(null);
      setError(null);
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  const removeShift = useMutation({
    mutationFn: (id) => api.del(`/shifts/${id}`),
    onMutate: () => setError(null),
    onSuccess: () => {
      setEditor(null);
      setError(null);
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  const publish = useMutation({
    mutationFn: () => api.post('/shifts/publish', { from, to }),
    onMutate: () => setError(null),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  const copyWeek = useMutation({
    mutationFn: (openShiftIds) =>
      api.post('/shifts/copy-week', {
        fromWeekStart: from,
        toWeekStart: addDaysISO(from, 7),
        openShiftIds,
      }),
    onMutate: () => setError(null),
    onSuccess: () => {
      setCopying(false);
      setPreview(null);
      setError(null);
      // Land on the week that was just built — otherwise the only sign it
      // worked is a toast on a screen that looks unchanged.
      const p = new URLSearchParams(searchParams);
      p.set('from', addDaysISO(from, 7));
      setSearchParams(p, { replace: true });
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  const decide = useMutation({
    mutationFn: ({ id, decision, note }) => api.post(`/time-off/${id}/${decision}`, { note }),
    onMutate: () => setError(null),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  const applyRoutine = useMutation({
    mutationFn: ({ routineId, dates, replace }) =>
      api.post(`/routines/${routineId}/apply`, { dates, replace }),
    onMutate: () => setError(null),
    onSuccess: () => {
      setPlanning(false);
      setPlanPreview(null);
      setError(null);
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  // Taking a day's plan back off.
  //
  // The opposite of applying a routine, and the only way to reach a day that
  // NEEDS nothing. Without it a day planned by mistake — or by the nightly
  // pass, which plans a month ahead — could be re-staffed but never emptied,
  // and the coverage warning on it would never go away.
  //
  // It removes what the day needs, not who is on it. Shifts already rostered
  // stay exactly where they are; they simply stop being measured against a
  // requirement nobody wants any more.
  const clearPlan = useMutation({
    mutationFn: (date) => api.del(`/routines/day/${date}`),
    onMutate: () => setError(null),
    onSuccess: () => { setClearingPlan(null); refresh(); },
    onError: (e) => setError(friendly(e)),
  });

  const askOff = useMutation({
    mutationFn: (body) => api.post('/time-off', body),
    onMutate: () => setError(null),
    onSuccess: () => {
      setAsking(false);
      setError(null);
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  async function openCopy() {
    setCopying(true);
    setPreview(null);
    setError(null);
    try {
      setPreview(
        await api.post('/shifts/copy-week', {
          fromWeekStart: from,
          toWeekStart: addDaysISO(from, 7),
          preview: true,
        }),
      );
    } catch (e) {
      setError(friendly(e));
    }
  }

  if (!canView) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="card p-8 text-center text-ink-500">The roster is for barn staff.</div>
      </div>
    );
  }

  const data = weekQ.data;
  const shifts = data?.shifts ?? [];
  const days = from ? (view === 'day' ? [from] : weekFrom(from)) : [];
  const waiting = unannouncedCount(shifts);
  const pending = requestsQ.data?.requests ?? [];
  // The union, not the sum: a job inside somebody's hours is not more hours.
  const totalMinutes = unionMinutes(shifts);

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">Shifts</h1>
          <p className="text-sm text-ink-500">
            {from ? (view === 'day' ? longDay(from) : rangeLabel(from, to)) : 'Loading…'}
            {shifts.length > 0 && (
              <>
                {' · '}
                {shifts.length} shift{shifts.length === 1 ? '' : 's'} · {formatMinutes(totalMinutes)}
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {/* Day and Week over the same data — the week to plan against, the
              day to work out where the holes are. */}
          <div className="flex rounded-lg border border-ink-300 overflow-hidden">
            {[
              ['week', 'Week'],
              ['day', 'Day'],
            ].map(([v, label]) => (
              <button
                key={v}
                onClick={() => setParams({ view: v === 'week' ? null : v })}
                aria-pressed={view === v}
                className={
                  'px-3 h-10 text-sm ' +
                  (view === v ? 'bg-hunter text-white' : 'bg-white text-ink-700 hover:text-hunter')
                }
              >
                {label}
              </button>
            ))}
          </div>
          <StepButton label={view === 'day' ? 'Previous day' : 'Previous week'} dir="prev" onClick={() => step(-1)} />
          {today && anchor !== today && (
            <button
              onClick={() => {
                const p = new URLSearchParams(searchParams);
                p.set('from', today);
                setSearchParams(p, { replace: true });
              }}
              className="px-3 h-10 rounded-lg border border-ink-300 bg-white text-sm text-ink-700 hover:border-hunter hover:text-hunter"
            >
              Today
            </button>
          )}
          <StepButton label={view === 'day' ? 'Next day' : 'Next week'} dir="next" onClick={() => step(1)} />
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {canEdit && (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setError(null);
                setPlanPreview(null);
                setPlanning(true);
              }}
              disabled={!from}
            >
              Plan {view === 'day' ? 'this day' : 'the week'}
            </button>
            {view === 'week' && (
              <button className="btn btn-secondary" onClick={openCopy} disabled={!from}>
                Copy to next week
              </button>
            )}
            {view === 'day' && (
              <div className="flex rounded-full border border-ink-200 overflow-hidden text-sm">
                {[
                  ['person', 'By person'],
                  ['work', 'By work'],
                ].map(([g, label]) => (
                  <button
                    key={g}
                    onClick={() => setParams({ by: g === 'person' ? null : g })}
                    aria-pressed={groupBy === g}
                    className={
                      'px-3 py-1.5 ' +
                      (groupBy === g ? 'bg-hunter text-white' : 'bg-white text-ink-700 hover:text-hunter')
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <button
              className="btn btn-secondary relative"
              onClick={() => setInbox(true)}
            >
              Requests
              {pending.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-amber-500 text-white text-xs">
                  {pending.length}
                </span>
              )}
            </button>
          </>
        )}
        <button className="btn btn-secondary" onClick={() => setAsking(true)}>
          {canEdit ? 'Mark myself off' : 'Ask for time off'}
        </button>

        {canEdit && (
          <div className="ml-auto flex items-center gap-3">
            {/* The count is the message. "Publish" alone gives no reason to
                press it and no way to tell whether it worked. */}
            <span className="text-sm text-ink-500">
              {waiting === 0
                ? 'Everyone has been told about this week.'
                : `${waiting} shift${waiting === 1 ? '' : 's'} nobody has been told about.`}
            </span>
            <button
              className="btn btn-primary"
              onClick={() => publish.mutate()}
              disabled={publish.isPending || waiting === 0}
            >
              {publish.isPending ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        )}
      </div>

      {publish.isSuccess && publish.data && (
        <p className="rounded-md bg-hunter/10 border border-hunter/30 px-3 py-2 text-sm text-hunter">
          Published {publish.data.published} shift{publish.data.published === 1 ? '' : 's'}.{' '}
          {publish.data.notified === 0
            ? 'Nobody needed telling.'
            : `${publish.data.notified} ${publish.data.notified === 1 ? 'person was' : 'people were'} sent their week.`}
        </p>
      )}

      {error && <p className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{error}</p>}

      {weekQ.isError && (
        <p className="card p-6 text-sm text-rose-700">
          The roster didn&apos;t load — {weekQ.error?.body?.error || weekQ.error?.message}
          {weekQ.error?.status ? ` (${weekQ.error.status})` : ''}.
        </p>
      )}

      {weekQ.isLoading || !from ? (
        <p className="card p-6 text-sm text-ink-400">Loading the week…</p>
      ) : (
        (() => {
          const common = {
            today,
            shifts,
            staff: data?.staff ?? [],
            timeOff: data?.timeOff ?? [],
            lessons: data?.lessons ?? [],
            canEdit,
            // `prefill` arrives when the click was on a demand band: the work
            // and the hours the routine asked for, which the editor should not
            // make somebody retype.
            onAdd: (date, userId, prefill) => {
              setError(null);
              setEditor({ date, userId, prefill });
            },
            onOpen: (shift) => {
              setError(null);
              setEditor({ shift, date: shift.date, userId: shift.userId });
            },
          };
          return view === 'day' ? (
            <DayGantt
              date={from}
              groupBy={groupBy}
              requirements={data?.requirements ?? []}
              onClearPlan={
                canEdit && (data?.requirements ?? []).some((r) => r.date === from)
                  ? () => setClearingPlan(from)
                  : null
              }
              // The barn's clock, not the browser's — a manager looking at a
              // California barn from the east coast should see the line where
              // the barn is.
              nowMinutes={data?.now ? toMinutes(data.now) : null}
              {...common}
            />
          ) : (
            <ShiftGrid days={days} coverage={data?.coverage ?? []} {...common} />
          );
        })()
      )}

      {!canEdit && (
        <p className="text-xs text-ink-400">
          This is the published schedule. A manager builds it and publishes when it&apos;s ready, so it can
          change before you hear about it.
        </p>
      )}

      {editor && (
        <ShiftEditor
          shift={editor.shift || null}
          date={editor.date}
          userId={editor.userId}
          prefill={editor.prefill || null}
          staff={data?.staff ?? []}
          types={data?.shiftTypes ?? []}
          timeOff={data?.timeOff ?? []}
          shifts={shifts}
          busy={saveShift.isPending || removeShift.isPending}
          error={error}
          onSave={(body) => saveShift.mutate({ id: editor.shift?.id, body })}
          onDelete={(s) => removeShift.mutate(s.id)}
          onClose={() => {
            setEditor(null);
            setError(null);
          }}
        />
      )}

      {copying && from && (
        <CopyWeekDialog
          from={from}
          to={addDaysISO(from, 7)}
          preview={preview}
          busy={copyWeek.isPending}
          error={error}
          onConfirm={(openShiftIds) => copyWeek.mutate(openShiftIds)}
          onClose={() => {
            setCopying(false);
            setPreview(null);
            setError(null);
          }}
        />
      )}

      {inbox && (
        <RequestsInbox
          requests={pending}
          busy={decide.isPending}
          error={error}
          onDecide={(r, decision, note) => decide.mutate({ id: r.id, decision, note })}
          onClose={() => {
            setInbox(false);
            setError(null);
          }}
        />
      )}

      {clearingPlan && (
        <ConfirmClearPlan
          date={clearingPlan}
          lines={(data?.requirements ?? []).filter((r) => r.date === clearingPlan)}
          staffed={(data?.shifts ?? []).filter((sh) => sh.date === clearingPlan).length}
          busy={clearPlan.isPending}
          error={error}
          onCancel={() => setClearingPlan(null)}
          onConfirm={() => clearPlan.mutate(clearingPlan)}
        />
      )}

      {planning && from && (
        <ApplyRoutineDialog
          days={days}
          routines={routinesQ.data?.routines ?? []}
          busy={applyRoutine.isPending}
          error={error}
          preview={planPreview}
          onPreview={async ({ routineId, dates, replace }) => {
            try {
              setPlanPreview(
                await api.post(`/routines/${routineId}/apply`, { dates, replace, preview: true }),
              );
            } catch (e) {
              setError(friendly(e));
            }
          }}
          onConfirm={(args) => applyRoutine.mutate(args)}
          onClose={() => {
            setPlanning(false);
            setPlanPreview(null);
            setError(null);
          }}
        />
      )}

      {asking && (
        <RequestTimeOff
          today={today || new Date().toISOString().slice(0, 10)}
          isApprover={canEdit}
          busy={askOff.isPending}
          error={error}
          onSubmit={(body) => askOff.mutate({ ...body, userId: user?.id })}
          onClose={() => {
            setAsking(false);
            setError(null);
          }}
        />
      )}
    </div>
  );
}

/** The same stepper the board uses — a real chevron and a thumb-sized target. */
function StepButton({ label, onClick, dir }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={
        'w-10 h-10 rounded-lg border border-ink-300 bg-white text-ink-700 shadow-sm ' +
        'inline-flex items-center justify-center transition-colors ' +
        'hover:border-hunter hover:text-hunter active:scale-95'
      }
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d={dir === 'prev' ? 'M12.5 4.5 7 10l5.5 5.5' : 'M7.5 4.5 13 10l-5.5 5.5'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/**
 * Taking a day's plan off.
 *
 * Two words that sound the same and are not: this removes what the day NEEDS,
 * and leaves who is ON it. A manager reading "clear the day" could reasonably
 * expect either, and finding out by pressing it is the wrong way round — so
 * the dialog says both halves before it does anything.
 */
function ConfirmClearPlan({ date, lines, staffed, busy, error, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        aria-label="Clear this day's plan"
        className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-100">
          <h2 className="font-medium text-lg leading-snug">Clear this day&apos;s plan</h2>
          <p className="text-sm text-ink-500">{fmtDate(date, 'dayLong')}</p>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          <p>
            {lines.length === 1
              ? 'One job comes off what this day needs:'
              : `${lines.length} jobs come off what this day needs:`}
          </p>
          <ul className="rounded-md border border-ink-200 divide-y divide-ink-100">
            {lines.slice(0, 6).map((r) => (
              <li key={r.id} className="px-3 py-1.5 flex justify-between gap-3">
                <span className="truncate">{r.typeName ?? 'Work'}</span>
                <span className="text-ink-400 tabular-nums shrink-0">
                  {timeRange(r.startTime, r.endTime)}
                </span>
              </li>
            ))}
            {lines.length > 6 && (
              <li className="px-3 py-1.5 text-ink-400">and {lines.length - 6} more</li>
            )}
          </ul>

          <p className="rounded-md bg-hunter/10 border border-hunter/25 px-3 py-2 text-hunter">
            {staffed === 0
              ? 'Nobody is rostered on this day, so nothing else changes.'
              : staffed === 1
                ? 'The one shift already rostered stays exactly where it is.'
                : `All ${staffed} shifts already rostered stay exactly where they are.`}{' '}
            This only stops the day being measured against a plan.
          </p>

          <p className="text-xs text-ink-400">
            Applying a routine again puts a plan back.
          </p>

          {error && <p className="text-sm text-rose-700">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-ink-100">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Keep it</button>
          <button type="button" className="btn-primary bg-rose-700 hover:bg-rose-800"
            onClick={onConfirm} disabled={busy}>
            {busy ? 'Clearing…' : 'Clear the plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
